/* De revisies van een offerte als tijdlijn, nieuwste boven: bedrag met het verloop
 * ten opzichte van de vorige revisie (een balk per revisie), de notitie, de PDF in de
 * projectmap met haar omschrijving, en wanneer welke revisie verstuurd is. Bovenaan de
 * werkversie: wat nu in de offerte staat. */

import { useState } from 'react';
import { CalendarCheck, ExternalLink, Eye, FileText, RotateCcw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter,
  AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { cn } from '@/lib/utils';
import { bedragVerloop, type Revisie } from '../data/revisies';
import { datum, euro, tijd } from '../../projectpagina/ui/opmaak';

const EURO_VERSCHIL = new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0, signDisplay: 'exceptZero' });

function Verschil({ v }: { v: number | null }) {
  if (v === null) return null;
  if (Math.abs(v) < 0.5) return <span className="text-xs text-muted-foreground">gelijk</span>;
  return <span className={cn('text-xs font-medium tabular-nums', v > 0 ? 'text-emerald-700' : 'text-red-700')}>{EURO_VERSCHIL.format(v)}</span>;
}

function Balk({ bedrag, max }: { bedrag: number | null; max: number }) {
  if (bedrag === null || max <= 0) return null;
  return (
    <span className="block h-1.5 w-full rounded-full bg-muted" aria-hidden>
      <span className="block h-1.5 rounded-full bg-slate-500" style={{ width: `${Math.max(2, (bedrag / max) * 100)}%` }} />
    </span>
  );
}

export function RevisieTijdlijn({
  revisies, laden, huidig, werkBedrag, verstuurd, onBekijk, onTerugzetten, bezig,
}: {
  revisies: Revisie[];
  laden: boolean;
  /** current_version van de offerte. */
  huidig: number;
  /** Totaal van de regels zoals ze nu zijn. */
  werkBedrag: number;
  verstuurd: { revisie: number | null; op: string | null; aan: string | null };
  onBekijk: (r: Revisie) => void;
  onTerugzetten: (r: Revisie) => Promise<void>;
  bezig: boolean;
}) {
  const [terug, setTerug] = useState<Revisie | null>(null);
  const verloop = bedragVerloop(revisies);
  const verschilPer = new Map(verloop.map((p) => [p.revisie.id, p.verschil]));
  const nieuwste = revisies[0];
  const max = Math.max(werkBedrag, ...revisies.map((r) => r.total_amount ?? 0));
  const werkVerschil = nieuwste?.total_amount != null ? werkBedrag - nieuwste.total_amount : null;
  const huidigBevroren = revisies.some((r) => r.version_number === huidig);

  if (laden) return <div className="h-32 animate-pulse rounded-xl bg-muted" aria-busy />;

  return (
    <>
      <ol className="flex flex-col rounded-xl border bg-background">
        <li className="grid grid-cols-[20px_minmax(0,1fr)_minmax(90px,140px)] gap-3 px-4 py-3">
          <span className="mt-1.5 h-2.5 w-2.5 rounded-full border-2 border-foreground bg-background" aria-hidden />
          <span className="flex min-w-0 flex-col">
            <span className="text-sm font-medium">Werkversie</span>
            <span className="text-xs text-muted-foreground">
              {huidigBevroren ? `Wat nu in de offerte staat, na revisie ${huidig}` : `Revisie ${huidig}, nog niet vastgelegd`}
            </span>
          </span>
          <span className="flex flex-col items-end gap-1">
            <span className="text-sm font-semibold tabular-nums">{euro(werkBedrag)}</span>
            {huidigBevroren && <Verschil v={werkVerschil} />}
            <Balk bedrag={werkBedrag} max={max} />
          </span>
        </li>

        {revisies.length === 0 && (
          <li className="border-t px-4 py-3 text-sm text-muted-foreground">
            Nog geen revisie vastgelegd. "Opslaan in projectmap" of "Revisie vastleggen" bevriest de stand van nu.
          </li>
        )}

        {revisies.map((r) => {
          const isVerstuurd = verstuurd.revisie === r.version_number && verstuurd.op;
          return (
            <li key={r.id} className="group grid grid-cols-[20px_minmax(0,1fr)_minmax(90px,140px)] gap-3 border-t px-4 py-3">
              <span className={cn('mt-1.5 h-2.5 w-2.5 rounded-full', r.file_name ? 'bg-foreground' : 'bg-muted-foreground/50')} aria-hidden />
              <span className="flex min-w-0 flex-col gap-1">
                <span className="flex flex-wrap items-baseline gap-x-2 text-sm">
                  <span className="font-medium">Revisie {r.version_number}</span>
                  <span className="text-xs text-muted-foreground">{datum(r.created_at)} {tijd(r.created_at)}</span>
                  {isVerstuurd && (
                    <span className="inline-flex items-center gap-1 rounded bg-blue-50 px-1.5 py-px text-[11px] font-medium text-blue-800">
                      <CalendarCheck className="h-3 w-3" aria-hidden />gemarkeerd als verstuurd · {datum(verstuurd.op)}{verstuurd.aan ? ` · aan ${verstuurd.aan}` : ''}
                    </span>
                  )}
                </span>
                {r.notes && <span className="whitespace-pre-line text-sm text-muted-foreground">{r.notes}</span>}
                {r.file_name ? (
                  <a
                    href={r.file_web_url ?? undefined}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex min-w-0 max-w-full items-center gap-1.5 self-start rounded-md border px-2 py-1 text-xs hover:bg-muted"
                    title={r.file_message ?? undefined}
                  >
                    <FileText className="h-3.5 w-3.5 shrink-0 text-red-700" aria-hidden />
                    <span className="truncate font-mono">{r.file_name}</span>
                    {r.file_web_url && <ExternalLink className="h-3 w-3 shrink-0 text-muted-foreground" aria-hidden />}
                  </a>
                ) : (
                  <span className="text-xs text-muted-foreground">Geen PDF in de projectmap</span>
                )}
                <span className="flex gap-1 opacity-100 sm:opacity-0 sm:transition-opacity sm:group-hover:opacity-100 sm:group-focus-within:opacity-100">
                  <Button type="button" variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={() => onBekijk(r)}>
                    <Eye className="mr-1 h-3.5 w-3.5" />Bekijken
                  </Button>
                  <Button type="button" variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={() => setTerug(r)} disabled={bezig}>
                    <RotateCcw className="mr-1 h-3.5 w-3.5" />Terugzetten
                  </Button>
                </span>
              </span>
              <span className="flex flex-col items-end gap-1">
                <span className="text-sm font-semibold tabular-nums">{euro(r.total_amount)}</span>
                <Verschil v={verschilPer.get(r.id) ?? null} />
                <Balk bedrag={r.total_amount} max={max} />
              </span>
            </li>
          );
        })}
      </ol>

      <AlertDialog open={terug !== null} onOpenChange={(o) => !o && setTerug(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Terugzetten naar revisie {terug?.version_number}?</AlertDialogTitle>
            <AlertDialogDescription>
              De regels, de titel en de bedragen van de werkversie worden vervangen door die van revisie {terug?.version_number}. De revisies zelf en de
              bestanden in de projectmap blijven zoals ze zijn.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuleren</AlertDialogCancel>
            <AlertDialogAction
              onClick={async () => {
                if (terug) await onTerugzetten(terug);
                setTerug(null);
              }}
            >
              Terugzetten
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
